// Cache Service - localStorage caching with "cache until logout" strategy
// GRO-44: Implement localStorage caching for Firestore data

const CACHE_KEYS = {
  USER: 'grownups-user-cache',
  TASKS: 'grownups-tasks-cache',
  JOURNAL: 'grownups-journal-cache',
  CANVAS: 'grownups-canvas-cache',
  LINKED_ACCOUNTS: 'grownups-linked-accounts-cache',
  CACHE_METADATA: 'grownups-cache-metadata'
};

class CacheService {
  // ============ CORE CACHE OPERATIONS ============

  /**
   * Get cached data by key
   * @param {string} key - Cache key
   * @returns {any|null} - Cached data or null if not found/expired
   */
  get(key) {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      return parsed.data;
    } catch (error) {
      console.error(`Cache get error for ${key}:`, error);
      return null;
    }
  }

  /**
   * Set cache data
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  set(key, data) {
    try {
      const cacheEntry = {
        data,
        cachedAt: Date.now(),
        version: 1
      };
      localStorage.setItem(key, JSON.stringify(cacheEntry));
      this.updateMetadata(key);
    } catch (error) {
      console.error(`Cache set error for ${key}:`, error);
      // If localStorage is full, try to clear old data
      if (error.name === 'QuotaExceededError') {
        this.clearOldestCache();
        try {
          localStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now(), version: 1 }));
        } catch (retryError) {
          console.error('Cache set failed after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Remove specific cache entry
   * @param {string} key - Cache key
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      this.removeFromMetadata(key);
    } catch (error) {
      console.error(`Cache remove error for ${key}:`, error);
    }
  }

  /**
   * Check if cache exists and is valid
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  }

  // ============ USER CACHE ============

  /**
   * Get cached user document
   * @returns {object|null}
   */
  getUser() {
    return this.get(CACHE_KEYS.USER);
  }

  /**
   * Cache user document
   * @param {object} userData - User data from Firestore
   */
  setUser(userData) {
    this.set(CACHE_KEYS.USER, userData);
  }

  /**
   * Update specific fields in cached user
   * @param {object} updates - Fields to update
   */
  updateUser(updates) {
    const current = this.getUser();
    if (current) {
      this.setUser({ ...current, ...updates });
    }
  }

  /**
   * Check if user is cached
   * @returns {boolean}
   */
  hasUser() {
    return this.has(CACHE_KEYS.USER);
  }

  // ============ LINKED ACCOUNTS CACHE ============

  /**
   * Get cached linked accounts
   * @returns {array|null}
   */
  getLinkedAccounts() {
    return this.get(CACHE_KEYS.LINKED_ACCOUNTS);
  }

  /**
   * Cache linked accounts
   * @param {array} accounts - Linked accounts from Firestore
   */
  setLinkedAccounts(accounts) {
    this.set(CACHE_KEYS.LINKED_ACCOUNTS, accounts);
  }

  /**
   * Add account to cache
   * @param {object} account - Account to add
   */
  addLinkedAccount(account) {
    const current = this.getLinkedAccounts() || [];
    if (!current.some(a => a.email === account.email)) {
      this.setLinkedAccounts([...current, account]);
    }
  }

  /**
   * Remove account from cache
   * @param {string} email - Email of account to remove
   */
  removeLinkedAccount(email) {
    const current = this.getLinkedAccounts() || [];
    this.setLinkedAccounts(current.filter(a => a.email !== email));
  }

  // ============ TASKS CACHE ============

  /**
   * Get cached task lists
   * @returns {array|null}
   */
  getTaskLists() {
    return this.get(CACHE_KEYS.TASKS);
  }

  /**
   * Cache task lists
   * @param {array} taskLists - Task lists from Firestore
   */
  setTaskLists(taskLists) {
    this.set(CACHE_KEYS.TASKS, taskLists);
  }

  /**
   * Update a single task list in cache
   * @param {string} taskListId - ID of task list to update
   * @param {object} taskList - Updated task list data
   */
  updateTaskList(taskListId, taskList) {
    const current = this.getTaskLists() || [];
    const index = current.findIndex(t => t.id === taskListId);
    if (index >= 0) {
      current[index] = taskList;
    } else {
      current.push(taskList);
    }
    this.setTaskLists(current);
  }

  /**
   * Remove task list from cache
   * @param {string} taskListId - ID of task list to remove
   */
  removeTaskList(taskListId) {
    const current = this.getTaskLists() || [];
    this.setTaskLists(current.filter(t => t.id !== taskListId));
  }

  // ============ JOURNAL CACHE ============

  /**
   * Get cached journal entries (indexed by date)
   * @returns {object|null} - { 'YYYY-MM-DD': journalData, ... }
   */
  getJournalEntries() {
    return this.get(CACHE_KEYS.JOURNAL) || {};
  }

  /**
   * Get single journal entry
   * @param {string} date - Date key (YYYY-MM-DD)
   * @returns {object|null}
   */
  getJournalEntry(date) {
    const entries = this.getJournalEntries();
    return entries[date] || null;
  }

  /**
   * Cache journal entries
   * @param {object} entries - Journal entries indexed by date
   */
  setJournalEntries(entries) {
    this.set(CACHE_KEYS.JOURNAL, entries);
  }

  /**
   * Update single journal entry in cache
   * @param {string} date - Date key (YYYY-MM-DD)
   * @param {object} entry - Journal entry data
   */
  setJournalEntry(date, entry) {
    const current = this.getJournalEntries();
    current[date] = entry;
    this.setJournalEntries(current);
  }

  /**
   * Remove single journal entry from cache
   * @param {string} date - Date key (YYYY-MM-DD)
   */
  removeJournalEntry(date) {
    const current = this.getJournalEntries();
    delete current[date];
    this.setJournalEntries(current);
  }

  // ============ CANVAS CACHE ============

  /**
   * Get cached canvas entries (indexed by composite key: "{view}-{periodKey}")
   * @returns {object} - { 'day-2026-01-28': canvasData, 'month-2026-01': canvasData, ... }
   */
  getCanvasEntries() {
    return this.get(CACHE_KEYS.CANVAS) || {};
  }

  /**
   * Get single canvas entry
   * @param {string} view - Calendar view type (day, week, month, year)
   * @param {string} periodKey - Period key
   * @returns {object|null}
   */
  getCanvasEntry(view, periodKey) {
    const entries = this.getCanvasEntries();
    const compositeKey = `${view}-${periodKey}`;
    return entries[compositeKey] || null;
  }

  /**
   * Cache canvas entries
   * @param {object} entries - Canvas entries indexed by composite key
   */
  setCanvasEntries(entries) {
    this.set(CACHE_KEYS.CANVAS, entries);
  }

  /**
   * Update single canvas entry in cache
   * @param {string} view - Calendar view type
   * @param {string} periodKey - Period key
   * @param {object} entry - Canvas data
   */
  setCanvasEntry(view, periodKey, entry) {
    const current = this.getCanvasEntries();
    const compositeKey = `${view}-${periodKey}`;
    current[compositeKey] = entry;
    this.setCanvasEntries(current);
  }

  /**
   * Remove single canvas entry from cache
   * @param {string} view - Calendar view type
   * @param {string} periodKey - Period key
   */
  removeCanvasEntry(view, periodKey) {
    const current = this.getCanvasEntries();
    const compositeKey = `${view}-${periodKey}`;
    delete current[compositeKey];
    this.setCanvasEntries(current);
  }

  // ============ CACHE MANAGEMENT ============

  /**
   * Clear all Firestore caches (called on logout)
   */
  clearAll() {
    Object.values(CACHE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Clear user-related caches only
   */
  clearUserCaches() {
    this.remove(CACHE_KEYS.USER);
    this.remove(CACHE_KEYS.TASKS);
    this.remove(CACHE_KEYS.JOURNAL);
    this.remove(CACHE_KEYS.CANVAS);
    this.remove(CACHE_KEYS.LINKED_ACCOUNTS);
  }

  /**
   * Get cache metadata (for debugging)
   * @returns {object}
   */
  getMetadata() {
    try {
      const meta = localStorage.getItem(CACHE_KEYS.CACHE_METADATA);
      return meta ? JSON.parse(meta) : {};
    } catch {
      return {};
    }
  }

  /**
   * Update cache metadata
   * @param {string} key - Cache key that was updated
   */
  updateMetadata(key) {
    try {
      const meta = this.getMetadata();
      meta[key] = {
        lastUpdated: Date.now(),
        size: localStorage.getItem(key)?.length || 0
      };
      localStorage.setItem(CACHE_KEYS.CACHE_METADATA, JSON.stringify(meta));
    } catch (error) {
      console.error('Failed to update cache metadata:', error);
    }
  }

  /**
   * Remove key from metadata
   * @param {string} key - Cache key to remove from metadata
   */
  removeFromMetadata(key) {
    try {
      const meta = this.getMetadata();
      delete meta[key];
      localStorage.setItem(CACHE_KEYS.CACHE_METADATA, JSON.stringify(meta));
    } catch (error) {
      console.error('Failed to remove from cache metadata:', error);
    }
  }

  /**
   * Clear oldest cache entry when storage is full
   */
  clearOldestCache() {
    const meta = this.getMetadata();
    const entries = Object.entries(meta);
    if (entries.length === 0) return;

    // Sort by lastUpdated, oldest first
    entries.sort((a, b) => (a[1].lastUpdated || 0) - (b[1].lastUpdated || 0));

    // Remove oldest entry
    const [oldestKey] = entries[0];
    this.remove(oldestKey);
  }

  /**
   * Get total cache size in bytes
   * @returns {number}
   */
  getTotalCacheSize() {
    let total = 0;
    Object.values(CACHE_KEYS).forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        total += item.length * 2; // UTF-16 = 2 bytes per char
      }
    });
    return total;
  }

  /**
   * Check if data needs refresh from Firestore
   * Used for manual refresh functionality
   * @param {string} key - Cache key
   * @param {number} maxAge - Max age in milliseconds (optional, for future TTL support)
   * @returns {boolean}
   */
  needsRefresh(key, maxAge = null) {
    if (!maxAge) return false; // Cache until logout by default

    try {
      const cached = localStorage.getItem(key);
      if (!cached) return true;

      const parsed = JSON.parse(cached);
      const age = Date.now() - (parsed.cachedAt || 0);
      return age > maxAge;
    } catch {
      return true;
    }
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Export cache keys for external use
export { CACHE_KEYS };

export default cacheService;
